import { FC, memo, type ReactNode } from 'react'

import { CheckIcon, Link, Text } from '@cash-me/react-components'

import { UnorderedList } from 'components/list'

import { ItemProps, TextItemProps, LinkItemProps, ListItemProps } from './help.model'
import * as S from './styles'

type ItemRendererProps = {
  item: ItemProps
  itemTextColor: string
  accordionArrowColor: string
  onLinkClick: (content: LinkItemProps) => void
}

const TextItemRenderer: FC<{ content: ReactNode; color: string }> = memo(({ content, color }) => (
  <S.Text size="body2" color={color}>
    {content}
  </S.Text>
))
TextItemRenderer.displayName = 'TextItemRenderer'

const ListItemRenderer: FC<{ content: ListItemProps[] }> = memo(({ content }) => <UnorderedList items={content} />)
ListItemRenderer.displayName = 'ListItemRenderer'

const LinkItemRenderer: FC<{ content: LinkItemProps; onClick: (content: LinkItemProps) => void }> = memo(
  ({ content, onClick }) => {
    const LinkIcon = content.icon
    return (
      <Link onClick={() => onClick(content)} size="default">
        <Text size="body2" weight="medium" color="var(--colors-blue-text-default)">
          {content.label}
        </Text>
        <LinkIcon size={12} />
      </Link>
    )
  }
)
LinkItemRenderer.displayName = 'LinkItemRenderer'

const ChecklistItemRenderer: FC<{ content: TextItemProps[]; color: string; iconColor: string }> = memo(
  ({ content, color, iconColor }) => (
    <ul>
      {content.map(({ id: itemId, text }) => (
        <S.ChecklistItem key={itemId}>
          <CheckIcon color={iconColor} className="checklist-icon" />
          <Text size="body2" color={color}>
            {text}
          </Text>
        </S.ChecklistItem>
      ))}
    </ul>
  )
)
ChecklistItemRenderer.displayName = 'ChecklistItemRenderer'

const NodeItemRenderer: FC<{ content: React.ReactNode }> = memo(({ content }) => (
  <S.NodeContent>{content}</S.NodeContent>
))
NodeItemRenderer.displayName = 'NodeItemRenderer'

export const HelpItemRenderer: FC<ItemRendererProps> = memo(
  ({ item, itemTextColor, accordionArrowColor, onLinkClick }) => {
    const { id, type, content } = item

    switch (type) {
      case 'text':
        return <TextItemRenderer key={id} content={content as ReactNode} color={itemTextColor} />
      case 'list':
        return <ListItemRenderer key={id} content={content as ListItemProps[]} />
      case 'link':
        return <LinkItemRenderer key={id} content={content as LinkItemProps} onClick={onLinkClick} />
      case 'checklist':
        return (
          <ChecklistItemRenderer
            key={id}
            content={content as TextItemProps[]}
            color={itemTextColor}
            iconColor={accordionArrowColor}
          />
        )
      case 'node':
        return <NodeItemRenderer key={id} content={content as React.ReactNode} />
      default:
        return null
    }
  }
)

HelpItemRenderer.displayName = 'HelpItemRenderer'
